import { useMemo } from 'react'

import { MarketplaceDbIntegration } from 'generated/resources/datatable.graphql'
import { ReadSyncValidationsQuery } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

export type SyncIssue = {
  title: string
  description: string
  buttonLabel?: string
  buttonUrl?: string
  onAction?: () => void
  affectedNames?: string[]
}

export type IssuesByKeyParams = {
  integrationName?: string
  datoId?: string | null
  companyId?: string
}

export type IssuesByKeyFactory = (params: IssuesByKeyParams) => Record<string, SyncIssue>

type SyncValidationResult = NonNullable<
  ReadSyncValidationsQuery['integrations']['syncValidationsConnection']['nodes'][0]
>['issues'][0]

const resolveIssues = (
  validationResults: SyncValidationResult[],
  issuesByKey: Record<string, SyncIssue>,
  marketplaceIntegration: MarketplaceDbIntegration | null,
  isValidation: boolean
): SyncIssue[] => {
  const validationResultIssues: SyncIssue[] = []
  const syncResultIssues: SyncIssue[] = []

  for (const result of validationResults) {
    const issue = issuesByKey[result.errorMessage.key]
    if (isValidation && !issue) continue
    const title = i18n.t(
      'bookkeepers.clients.payroll_cycles.sync_status_stats.result_issues.api_error.title',
      {
        integration_name: marketplaceIntegration?.displayName,
      }
    )
    const description =
      result.errorMessage.value ||
      i18n.t('bookkeepers.clients.payroll_cycles.sync_status_stats.result_issues.unknown_error')

    const entry: SyncIssue = {
      title: issue?.title ?? title,
      description: issue?.description ?? description,
      buttonLabel: issue?.buttonLabel,
      buttonUrl: issue?.buttonUrl,
      onAction: issue?.onAction,
      affectedNames: result.affectedNames,
    }

    if (issue) {
      validationResultIssues.push(entry)
    } else {
      syncResultIssues.push(entry)
    }
  }

  return [...validationResultIssues, ...syncResultIssues]
}

export const useSyncIssues = (
  validationResults: SyncValidationResult[],
  marketplaceIntegration: MarketplaceDbIntegration | null,
  issuesByKeyFactory: IssuesByKeyFactory,
  isValidation: boolean = true,
  companyId?: string
): SyncIssue[] => {
  const issuesByKey = issuesByKeyFactory({
    integrationName: marketplaceIntegration?.displayName,
    datoId: marketplaceIntegration?.datoId,
    companyId,
  })

  return resolveIssues(validationResults, issuesByKey, marketplaceIntegration, isValidation)
}

export const useIssuesWithModalClose = (issues: SyncIssue[], closeModal: () => void) => {
  return useMemo(
    () =>
      issues.map((issue) => {
        if (!issue.onAction) return issue
        const { onAction } = issue
        return {
          ...issue,
          onAction: () => {
            onAction()
            closeModal()
          },
        }
      }),
    [issues, closeModal]
  )
}
